<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { RouteLocationNamedRaw } from 'vue-router'

import SidebarLink from '~/components/atoms/SidebarLink.vue'
import BaseSidebar from '~/components/molecules/BaseSidebar.vue'
import CurrentLocationDropdown from '~/components/organisms/CurrentLocationDropdown.vue'
import { useCurrentUser } from '~/composables/useCurrentUser'
import { useOrganization } from '~/composables/useOrganization'

const { currentUser } = useCurrentUser()
const { t } = useI18n()

const route = useRoute()
const currentRouteName = computed(() => route.name?.toString())
const isActiveMenu = (name: string) => {
  if (!currentRouteName.value) return false
  const currentParts = currentRouteName.value.split('-')
  const nameParts = name.split('-')
  return currentParts.slice(0, 4).every((part, i) => part === nameParts[i])
}

const routeParams = computed(() => {
  const organizationId = route.params.organizationId
  const locationId = route.params.locationId
  if (typeof organizationId !== 'string' || typeof locationId !== 'string') return
  return { organizationId, locationId }
})

const { organization } = useOrganization()

type MenuItem = { to: RouteLocationNamedRaw; text: string }

const masterMenu = computed(() => {
  const params = routeParams.value
  if (!params) return []

  const menus: MenuItem[] = []
  if (currentUser.value?.canEditLocation) {
    menus.push({
      to: { name: 'organizationId-locations-locationId-checkFiles', params },
      text: t('チェックファイル'),
    })
    menus.push({
      to: { name: 'organizationId-locations-locationId-checkSheets', params },
      text: t('チェックシート'),
    })
    menus.push({
      to: { name: 'organizationId-locations-locationId-timeboxes', params },
      text: t('詳細設定'),
    })
  }
  return menus
})

const locationSettingsMenus = computed(() => {
  const params = routeParams.value
  if (!params) return []

  return [
    {
      to: { name: 'organizationId-locations-locationId-edit', params },
      text: t('情報'),
    },
    {
      to: { name: 'organizationId-locations-locationId-accounts-locationAccounts', params },
      text: t('作業者'),
    },
    {
      to: { name: 'organizationId-locations-locationId-users', params },
      text: t('管理者'),
    },
    {
      to: { name: 'organizationId-locations-locationId-ticketSharingGroups', params },
      text: t('共有グループ'),
    },
    organization.value?.has_security_option && {
      to: { name: 'organizationId-locations-locationId-security-ipRestrictions', params },
      text: t('セキュリティ設定'),
    },
  ].filter(Boolean) as MenuItem[]
})
</script>

<template>
  <div>
    <CurrentLocationDropdown class="locations-dropdown" />
    <aside v-if="routeParams" class="menu">
      <ul>
        <li class="sidebar-menu-item" :class="{ active: isActiveMenu('organizationId-locations-locationId-myPage') }">
          <SidebarLink
            :to="{ name: 'organizationId-locations-locationId-myPage', params: routeParams }"
            :text="$t('マイページ')"
          />
        </li>

        <li v-if="masterMenu.length > 0">
          <BaseSidebar :title="$t('マスタ')" :menus="masterMenu" />
        </li>

        <li
          class="sidebar-menu-item"
          :class="{ active: isActiveMenu('organizationId-locations-locationId-monthlyWorkFiles') }"
        >
          <SidebarLink
            :to="{ name: 'organizationId-locations-locationId-monthlyWorkFiles', params: routeParams }"
            :text="$t('作業記録')"
          />
        </li>

        <li class="sidebar-menu-item" :class="{ active: isActiveMenu('organizationId-locations-locationId-tickets') }">
          <SidebarLink
            :to="{ name: 'organizationId-locations-locationId-tickets', params: routeParams }"
            :text="$t('チケット')"
          />
        </li>

        <li v-if="currentUser?.role !== 'WORK_ADMIN'">
          <BaseSidebar :title="$t('拠点設定')" :menus="locationSettingsMenus" />
        </li>
      </ul>
    </aside>
  </div>
</template>

<style lang="scss" scoped>
.locations-dropdown {
  margin: 16px;
}
.sidebar-menu-item {
  border-left: 4px solid transparent;
  a {
    display: block;
    color: var(--semantic-tokens-neutral-default-text);
    padding: 10px 10px 10px 20px;
  }
  &:not(.active):hover {
    background-color: var(--semantic-tokens-neutral-xweak-surface);
  }
}
.active {
  background-color: var(--semantic-tokens-brand-weak-surface);
  border-left: 4px solid var(--semantic-tokens-brand-strong-border);
  a {
    font-weight: bold;
    color: var(--semantic-tokens-brand-strong-border);
  }
}
</style>
